import React, { useEffect, useRef, useState } from 'react';
import { Doughnut } from 'react-chartjs-2';
import siteAnalyticsService, { AcquisitionResponse, DimensionRow } from '../../services/siteAnalytics.service';
import { useDateRange, RangeSelector, LoadError, fa, fmtDuration } from './useDateRange';
import { COLORS, defaultDoughnutOptions } from './chartConfig';
import './analytics.css';

const SiteAnalyticsAcquisition: React.FC = () => {
  const dr = useDateRange('30d');
  const [data, setData] = useState<AcquisitionResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const reqIdRef = useRef(0);

  const load = async () => {
    const reqId = ++reqIdRef.current;
    setLoading(true);
    setErr(null);
    try {
      const r = await siteAnalyticsService.getAcquisition(dr.range);
      if (reqId !== reqIdRef.current) return;
      setData(r);
    } catch (e: any) {
      if (reqId !== reqIdRef.current) return;
      setErr(e?.message || 'خطا در دریافت داده');
    } finally {
      if (reqId === reqIdRef.current) setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dr.range.from, dr.range.to, dr.refreshKey]);

  const MEDIUM_FA: Record<string, string> = {
    direct: 'مستقیم',
    organic: 'موتور جستجو',
    referral: 'سایت دیگر',
    social: 'شبکه اجتماعی',
    paid: 'تبلیغ پولی',
    email: 'ایمیل',
    other: 'سایر',
  };

  const dimensionTable = (title: string, rows: DimensionRow[], extraCol?: (r: DimensionRow) => string) => (
    <div className="analytics-section">
      <div className="analytics-section-header">
        <h3>{title}</h3>
      </div>
      {!rows || rows.length === 0 ? (
        <div className="analytics-empty">داده‌ای نیست</div>
      ) : (
        <table className="analytics-table">
          <thead>
            <tr>
              <th>عنوان</th>
              <th>سشن</th>
              <th>کاربر</th>
              <th>بازدید صفحه</th>
              <th>Bounce</th>
              <th>میانگین مدت</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.value || '(empty)'}>
                <td>{extraCol ? extraCol(r) : r.value || '(نامشخص)'}</td>
                <td>{fa(r.sessions)}</td>
                <td>{fa(r.visitors)}</td>
                <td>{fa(r.pageViews)}</td>
                <td>{fa(r.bounceRate)}٪</td>
                <td>{fmtDuration(r.avgDuration)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );

  const doughnutData = data
    ? {
        labels: data.byMedium.map((r) => MEDIUM_FA[r.value] || r.value || 'نامشخص'),
        datasets: [
          {
            data: data.byMedium.map((r) => r.sessions),
            backgroundColor: COLORS,
          },
        ],
      }
    : null;

  return (
    <div className="analytics-page">
      <div className="analytics-header">
        <h2>منابع ترافیک</h2>
        <div className="analytics-controls">
          <RangeSelector {...dr} />
          <button onClick={dr.refresh} disabled={loading}>
            {loading ? 'در حال بارگذاری…' : 'بروزرسانی'}
          </button>
        </div>
      </div>

      {err && <LoadError message={err} onRetry={load} />}
      {loading && !data && <div className="analytics-loading">در حال بارگذاری…</div>}

      {data && (
        <>
          {/* 🆕 کانال‌های ورودی — تفکیک کلی (مستقیم/جستجو/شبکه اجتماعی/پیام‌رسان/…) */}
          <div className="analytics-section">
            <div className="analytics-section-header">
              <h3>کانال‌های ورودی</h3>
            </div>
            {!data.byChannel || data.byChannel.length === 0 ? (
              <div className="analytics-empty">داده‌ای نیست</div>
            ) : (
              <div className="analytics-kpi-grid" style={{ marginBottom: 0 }}>
                {data.byChannel.map((c, i) => (
                  <div key={c.channel} className={`analytics-kpi-card ${['', 'green', 'orange', 'purple', 'teal', 'red'][i % 6]}`}>
                    <div className="accent" />
                    <div className="label">{c.label}</div>
                    <div className="value">{fa(c.sessions)}</div>
                    <span className="change flat">
                      {fa(c.visitors)} بازدیدکننده · نرخ پرش {fa(c.bounceRate)}٪ · {fmtDuration(c.avgDuration)}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* 🆕 منابع ورودی دقیق — بله/روبیکا/ایتا/تلگرام/گوگل/اینستاگرام/… */}
          <div className="analytics-section">
            <div className="analytics-section-header">
              <h3>منابع ورودی (تفکیک دقیق)</h3>
            </div>
            {!data.bySource || data.bySource.length === 0 ? (
              <div className="analytics-empty">داده‌ای نیست</div>
            ) : (
              <table className="analytics-table">
                <thead>
                  <tr>
                    <th>منبع</th>
                    <th>کانال</th>
                    <th>سشن</th>
                    <th>بازدیدکننده</th>
                    <th>بازدید صفحه</th>
                    <th>نرخ پرش</th>
                    <th>میانگین مدت</th>
                  </tr>
                </thead>
                <tbody>
                  {data.bySource.map((s) => (
                    <tr key={`${s.channel}|${s.source}`}>
                      <td style={{ fontWeight: 600 }}>{s.label || s.source || '(نامشخص)'}</td>
                      <td>
                        <span
                          style={{
                            display: 'inline-block',
                            padding: '2px 10px',
                            borderRadius: 12,
                            fontSize: 11,
                            background: 'var(--color-bg-default)',
                            border: '1px solid var(--color-border)',
                            color: 'var(--color-text-secondary)',
                          }}
                        >
                          {s.channelLabel || s.channel}
                        </span>
                      </td>
                      <td>{fa(s.sessions)}</td>
                      <td>{fa(s.visitors)}</td>
                      <td>{fa(s.pageViews)}</td>
                      <td>{fa(s.bounceRate)}٪</td>
                      <td>{fmtDuration(s.avgDuration)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>

          <div className="analytics-grid-2col">
            <div className="analytics-section">
              <div className="analytics-section-header">
                <h3>توزیع منابع ترافیک</h3>
              </div>
              <div className="analytics-chart-container">
                {doughnutData && doughnutData.datasets[0].data.length > 0 ? (
                  <Doughnut data={doughnutData} options={defaultDoughnutOptions} />
                ) : (
                  <div className="analytics-empty">داده‌ای نیست</div>
                )}
              </div>
            </div>
            <div className="analytics-section">
              <div className="analytics-section-header">
                <h3>شبکه‌های اجتماعی</h3>
              </div>
              {data.topSocial.length === 0 ? (
                <div className="analytics-empty">سشنی از شبکه‌های اجتماعی نیست</div>
              ) : (
                <table className="analytics-table">
                  <thead>
                    <tr>
                      <th>شبکه</th>
                      <th>سشن</th>
                      <th>کاربر</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.topSocial.map((s) => (
                      <tr key={s.socialNetwork || ''}>
                        <td>{s.socialNetwork || '(نامشخص)'}</td>
                        <td>{fa(s.sessions)}</td>
                        <td>{fa(s.visitors)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </div>

          {data.attribution && (
            <div className="analytics-section">
              <div className="analytics-section-header">
                <h3>مدل اِسناد (Attribution) — تبدیل‌ها بر اساس منبع</h3>
              </div>
              <div style={{ fontSize: 12, color: 'var(--color-text-secondary)', marginBottom: 12, lineHeight: 1.7 }}>
                <strong>First-touch</strong> = منبعی که کاربر را برای <em>اولین بار</em> آورد ·{' '}
                <strong>Last-touch</strong> = منبعی که تبدیل در آن <em>بسته شد</em>. مقایسه نشان می‌دهد کدام کانال «کشف» می‌کند و کدام «می‌فروشد».
                <br />
                «تبدیل» = همهٔ اقدام‌های مهم (ورود، ثبت‌نام، افزودن به سبد، …) · «خرید» و «درآمد» فقط از خریدهای واقعی (بدون تکرار، بر اساس شمارهٔ سفارش).
              </div>
              <div className="analytics-grid-2col">
                {([
                  ['اولین تماس (First-touch)', data.attribution.firstTouch],
                  ['آخرین تماس (Last-touch)', data.attribution.lastTouch],
                ] as const).map(([label, rows]) => (
                  <div key={label}>
                    <h4 style={{ margin: '0 0 8px' }}>{label}</h4>
                    {!rows || rows.length === 0 ? (
                      <div className="analytics-empty">تبدیلی ثبت نشده</div>
                    ) : (
                      <table className="analytics-table">
                        <thead>
                          <tr>
                            <th>منبع</th>
                            <th>تبدیل</th>
                            <th>خرید</th>
                            <th>درآمد</th>
                          </tr>
                        </thead>
                        <tbody>
                          {rows.map((r) => (
                            <tr key={r.medium || '(empty)'}>
                              <td>{MEDIUM_FA[r.medium] || r.medium || '(نامشخص)'}</td>
                              <td>{fa(r.conversions)}</td>
                              <td>{fa(r.purchases || 0)}</td>
                              <td>{r.revenue ? `${fa(r.revenue)} تومان` : '—'}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {dimensionTable('بر اساس منبع (Medium)', data.byMedium, (r) => MEDIUM_FA[r.value] || r.value || '(نامشخص)')}
          {dimensionTable('سایت‌های ارجاع‌دهنده (Referrers)', data.topReferrers)}
          {dimensionTable('کمپین‌های UTM', data.topUtm)}
          {dimensionTable('صفحات ورود (Landing Pages)', data.topLanding)}
        </>
      )}
    </div>
  );
};

export default SiteAnalyticsAcquisition;
